"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useMemo, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { Drawer } from "vaul";
import { classNames } from "@/lib/utils";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useDemoContext } from "@/contexts/DemoContext";
import { useGetAllAccountsForUser } from "@/hooks/queries/accounts";
import { useLocalizedAppPath } from "@/contexts/AppLocaleContext";
import { UserAvatar, preloadAvatar } from "@/components/ui/user-avatar";
import { ChevronUpDownIcon, PlusIcon, Cog6ToothIcon, ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import { AccountByIdData } from "@/hooks/queries/accounts";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { proxyImageUrl } from "@/lib/image-proxy";
import { TrendingUpIcon } from "@/components/ui/trending-up";
import { SparklesIcon } from "@/components/ui/sparkles-icon";
import { RocketIcon } from "@/components/ui/rocket-icon";

// ============================================
// TYPES
// ============================================
type AccountWithPlatform = AccountByIdData & {
  platform?: "instagram" | "tiktok";
  account_type?: "trial" | "go" | "grow" | "glory" | "archived";
};
interface SidebarAccountSelectorProps {
  className?: string;
}

// ============================================
// LOCAL STORAGE KEY FOR SELECTED ACCOUNT
// ============================================
const SELECTED_ACCOUNT_KEY = "sidebar-selected-account-id";
export function getStoredSelectedAccountId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(SELECTED_ACCOUNT_KEY);
  } catch {
    return null;
  }
}
function setStoredSelectedAccountId(accountId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
  } catch {
    // localStorage not available
  }
}

// ============================================
// SKELETON LOADER
// Matches exact dimensions of AccountTrigger to prevent layout shift
// ============================================
function AccountSelectorSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classNames("relative flex w-full items-center gap-2.5 px-2.5 py-2.5", "rounded-xl");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="h-7 w-7 shrink-0 rounded-lg" style={{
      background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-shimmer 1.5s ease-in-out infinite"
    }} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="min-w-0 flex-1"><div className="h-5 w-28 rounded-md" style={{
        background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
        backgroundSize: "200% 100%",
        animation: "skeleton-shimmer 1.5s ease-in-out infinite",
        animationDelay: "0.1s"
      }} /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={t0} aria-hidden="true">{t1}{t2}<div className="h-4 w-4 shrink-0 rounded" style={{
        background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
        backgroundSize: "200% 100%",
        animation: "skeleton-shimmer 1.5s ease-in-out infinite",
        animationDelay: "0.2s"
      }} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// ============================================
// UTILITIES
// ============================================
function formatFollowers(count: number | null | undefined): string {
  if (!count) return "0";
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return `${count}`;
}

// ============================================
// PLAN BADGE (Memoized)
// Premium badges with solid backgrounds, white text, animated icons on hover
// ============================================
const PlanBadge = memo(function PlanBadge(t0) {
  const $ = _c(20);
  const {
    accountType,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  const goIconRef = useRef(null);
  const growIconRef = useRef(null);
  const gloryIconRef = useRef(null);
  const iconSize = size === "compact" ? 12 : 13;
  const isCompact = size === "compact";
  let t2;
  if ($[0] !== accountType || $[1] !== iconSize || $[2] !== isCompact) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const baseClasses = classNames("inline-flex items-center justify-center gap-1.5 rounded-none uppercase tracking-wider transition-all duration-150", isCompact ? "h-[20px] px-[7px] text-[10px] font-extrabold" : "h-[22px] px-2.5 text-[11px] font-extrabold");
      if (accountType === "trial") {
        let t3;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <span className="translate-y-[0.5px]">Trial</span>;
          $[4] = t3;
        } else {
          t3 = $[4];
        }
        t2 = <span className={classNames(baseClasses, "bg-amber-100 text-amber-700")}>{t3}</span>;
        break bb0;
      }
      if (accountType === "basic" || accountType === "go") {
        let t3;
        let t4;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = () => goIconRef.current?.startAnimation?.();
          t4 = () => goIconRef.current?.stopAnimation?.();
          $[5] = t3;
          $[6] = t4;
        } else {
          t3 = $[5];
          t4 = $[6];
        }
        let t5;
        if ($[7] !== iconSize) {
          t5 = <TrendingUpIcon ref={goIconRef} size={iconSize} />;
          $[7] = iconSize;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        let t6;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <span className="translate-y-[0.5px]">Go</span>;
          $[9] = t6;
        } else {
          t6 = $[9];
        }
        t2 = <span className={classNames(baseClasses, "bg-[#3b82f6] text-white hover:bg-[#2563eb]")} onMouseEnter={t3} onMouseLeave={t4}>{t5}{t6}</span>;
        break bb0;
      }
      if (accountType === "pro" || accountType === "grow") {
        let t3;
        let t4;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = () => growIconRef.current?.startAnimation?.();
          t4 = () => growIconRef.current?.stopAnimation?.();
          $[10] = t3;
          $[11] = t4;
        } else {
          t3 = $[10];
          t4 = $[11];
        }
        let t5;
        if ($[12] !== iconSize) {
          t5 = <SparklesIcon ref={growIconRef} size={iconSize} />;
          $[12] = iconSize;
          $[13] = t5;
        } else {
          t5 = $[13];
        }
        let t6;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <span className="translate-y-[0.5px]">Grow</span>;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        t2 = <span className={classNames(baseClasses, "bg-[#ec4899] text-white hover:bg-[#db2777]")} onMouseEnter={t3} onMouseLeave={t4}>{t5}{t6}</span>;
        break bb0;
      }
      if (accountType === "turbo" || accountType === "glory") {
        let t3;
        let t4;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = () => gloryIconRef.current?.startAnimation?.();
          t4 = () => gloryIconRef.current?.stopAnimation?.();
          $[15] = t3;
          $[16] = t4;
        } else {
          t3 = $[15];
          t4 = $[16];
        }
        let t5;
        if ($[17] !== iconSize) {
          t5 = <RocketIcon ref={gloryIconRef} size={iconSize} />;
          $[17] = iconSize;
          $[18] = t5;
        } else {
          t5 = $[18];
        }
        let t6;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <span className="translate-y-[0.5px]">Glory</span>;
          $[19] = t6;
        } else {
          t6 = $[19];
        }
        t2 = <span className={classNames(baseClasses, "bg-[#059669] text-white hover:bg-[#047857]")} onMouseEnter={t3} onMouseLeave={t4}>{t5}{t6}</span>;
        break bb0;
      }
    }
    $[0] = accountType;
    $[1] = iconSize;
    $[2] = isCompact;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return null;
});

// ============================================
// PLATFORM ICON
// Badge-style: Just the icon with gradient fill, no white container
// ============================================
function PlatformIcon(t0) {
  const $ = _c(8);
  const {
    platform,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? 14 : t1;
  if (platform === "instagram") {
    let t2;
    if ($[0] !== className) {
      t2 = classNames("rounded-[3px]", className);
      $[0] = className;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== size || $[3] !== t2) {
      t3 = <Image src="/img/icons/instagram.svg" width={size} height={size} alt="" className={t2} />;
      $[2] = size;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (platform === "tiktok") {
    let t2;
    if ($[5] !== className || $[6] !== size) {
      t2 = <Image src="/img/icons/tiktok.svg" width={size} height={size} alt="" className={className} />;
      $[5] = className;
      $[6] = size;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  return null;
}

// ============================================
// TRIGGER BUTTON (Memoized)
// Hover effect matches sidebar menu items exactly
// ============================================
const AccountTrigger = memo(function AccountTrigger(t0) {
  const $ = _c(10);
  const {
    account
  } = t0;
  const [isHovered, setIsHovered] = useState(false);
  const t1 = isHovered ? "rgba(255,255,255,0.6)" : "transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      backgroundColor: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const dynamicStyle = t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsHovered(true);
    t4 = () => setIsHovered(false);
    t5 = classNames("relative flex w-full items-center gap-3 px-2.5 py-3", "rounded-xl", "transition-all duration-150 ease-out", "active:scale-[0.99]", "focus:outline-none focus-visible:outline-none");
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== account) {
    t6 = account ? <><div className="relative shrink-0"><UserAvatar src={proxyImageUrl(account.avatar_url)} username={account.username} size={32} rounded="lg" ring={false} /><div className="absolute -bottom-1 -right-1"><PlatformIcon platform={account.platform} size={14} className="drop-shadow-sm" /></div></div><div className="min-w-0 flex-1 text-left"><span className="block truncate text-[15px] font-semibold tracking-[-0.01em] text-[#141414] transition-colors duration-150">{account.username}</span><div className="-mt-0.5"><PlanBadge accountType={account.account_type} size="compact" /></div></div><ChevronUpDownIcon className="h-4 w-4 shrink-0 text-[#141414]/60 transition-colors duration-150 hover:text-[#141414]" /></> : <span className="text-base text-[#141414]">Select account</span>;
    $[5] = account;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== dynamicStyle || $[8] !== t6) {
    t7 = <button type="button" onMouseEnter={t3} onMouseLeave={t4} className={t5} style={dynamicStyle}>{t6}</button>;
    $[7] = dynamicStyle;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
});

// ============================================
// ACCOUNT ROW (Memoized to prevent re-renders)
// ============================================
interface AccountRowProps {
  account: AccountWithPlatform;
  isSelected?: boolean;
  onClick?: () => void;
}
const AccountRow = memo(function AccountRow(t0) {
  const $ = _c(34);
  const {
    account,
    isSelected,
    onClick
  } = t0;
  const t1 = isSelected ? "bg-[#f6f6f4]" : "hover:bg-[#f6f6f4]/70";
  let t2;
  if ($[0] !== t1) {
    t2 = classNames("group flex w-full items-center gap-3 rounded-xl px-3 py-2 transition-all duration-150", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== account.avatar_url) {
    t3 = proxyImageUrl(account.avatar_url);
    $[2] = account.avatar_url;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== account.username || $[5] !== t3) {
    t4 = <UserAvatar src={t3} username={account.username} size={40} rounded="xl" ring={false} />;
    $[4] = account.username;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== account.platform) {
    t5 = <div className="absolute -bottom-0.5 -right-0.5"><PlatformIcon platform={account.platform} size={15} className="drop-shadow-sm" /></div>;
    $[7] = account.platform;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="relative shrink-0">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== account.username) {
    t7 = <span className="block truncate text-[15px] font-semibold tracking-[-0.01em] text-[#141414]">{account.username}</span>;
    $[12] = account.username;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== account.account_type) {
    t8 = <PlanBadge accountType={account.account_type} size="compact" />;
    $[14] = account.account_type;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== account.followers) {
    t9 = formatFollowers(account.followers);
    $[16] = account.followers;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <span className="text-[13px] tracking-[-0.01em] text-[#141414]/50"><span className="font-medium text-[#141414]/70">{t9}</span> followers</span>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t8) {
    t11 = <div className="mt-0.5 flex items-center gap-1.5">{t8}{t10}</div>;
    $[20] = t10;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t7) {
    t12 = <div className="min-w-0 flex-1 text-left">{t7}{t11}</div>;
    $[23] = t11;
    $[24] = t7;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== isSelected) {
    t13 = isSelected && <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-900"><svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>;
    $[26] = isSelected;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== onClick || $[29] !== t12 || $[30] !== t13 || $[31] !== t2 || $[32] !== t6) {
    t14 = <button onClick={onClick} className={t2}>{t6}{t12}{t13}</button>;
    $[28] = onClick;
    $[29] = t12;
    $[30] = t13;
    $[31] = t2;
    $[32] = t6;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
});

// ============================================
// DROPDOWN CONTENT (Memoized)
// ============================================
interface DropdownContentProps {
  accounts: AccountWithPlatform[];
  selectedAccount: AccountWithPlatform | null;
  onSelectAccount: (account: AccountWithPlatform) => void;
  onConnectAccount: () => void;
  onClose: () => void;
  settingsHref: string;
}
const DropdownContent = memo(function DropdownContent(t0) {
  const $ = _c(46);
  const {
    accounts,
    selectedAccount,
    onSelectAccount,
    onConnectAccount,
    onClose,
    settingsHref
  } = t0;
  const {
    isDemoMode
  } = useDemoContext();
  selectedAccount?.id;
  let t1;
  if ($[0] !== accounts || $[1] !== selectedAccount?.id) {
    let t2;
    if ($[3] !== selectedAccount?.id) {
      t2 = a => a.id !== selectedAccount?.id;
      $[3] = selectedAccount?.id;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = accounts.filter(t2);
    $[0] = accounts;
    $[1] = selectedAccount?.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const otherAccounts = t1;
  const scrollRef = useRef(null);
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(false);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = el;
      setCanScrollTop(scrollTop > 0);
      setCanScrollBottom(scrollTop + clientHeight < scrollHeight - 5);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const checkScroll = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      checkScroll();
      const timer = setTimeout(checkScroll, 100);
      return () => clearTimeout(timer);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== accounts) {
    t4 = [accounts, checkScroll];
    $[7] = accounts;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== onClose || $[10] !== onSelectAccount) {
    t5 = account => {
      onSelectAccount(account);
      onClose();
    };
    $[9] = onClose;
    $[10] = onSelectAccount;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleAccountClick = t5;
  let t6;
  if ($[12] !== onClose || $[13] !== onConnectAccount) {
    t6 = () => {
      onConnectAccount();
      onClose();
    };
    $[12] = onClose;
    $[13] = onConnectAccount;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleConnectClick = t6;
  const t7 = canScrollTop ? "opacity-100" : "opacity-0";
  let t8;
  if ($[15] !== t7) {
    t8 = classNames("pointer-events-none absolute left-0 right-0 top-0 z-10 h-6 bg-gradient-to-b from-white to-transparent transition-opacity duration-200", t7);
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <div className={t8} />;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== selectedAccount) {
    t10 = selectedAccount && <AccountRow account={selectedAccount} isSelected={true} />;
    $[19] = selectedAccount;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== handleAccountClick || $[22] !== otherAccounts) {
    t11 = otherAccounts.length > 0 && <><div className="px-3 pb-1 pt-3"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#141414]/40">Switch to</span></div>{otherAccounts.map(account_0 => <AccountRow key={account_0.id} account={account_0} onClick={() => handleAccountClick(account_0)} />)}</>;
    $[21] = handleAccountClick;
    $[22] = otherAccounts;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <div ref={scrollRef} onScroll={checkScroll} className="scrollbar-hide max-h-[256px] overflow-y-auto p-1.5">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = canScrollBottom ? "opacity-100" : "opacity-0";
  let t14;
  if ($[27] !== t13) {
    t14 = classNames("pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent transition-opacity duration-200", t13);
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <div className={t14} />;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t15 || $[33] !== t9) {
    t16 = <div className="relative">{t9}{t12}{t15}</div>;
    $[31] = t12;
    $[32] = t15;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PlusIcon className="h-4 w-4" strokeWidth={2.5} />;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== handleConnectClick) {
    t18 = <div className="border-t border-[#141414]/[0.06] p-3"><button onClick={handleConnectClick} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#141414] text-[14px] font-semibold text-white transition-all duration-200 hover:bg-[#141414]/90 active:scale-[0.98]">{t17}Add account</button></div>;
    $[36] = handleConnectClick;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isDemoMode || $[39] !== onClose || $[40] !== settingsHref) {
    t19 = !isDemoMode && <div className="border-t border-[#141414]/[0.06] p-1.5"><a href={settingsHref} onClick={onClose} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-medium text-[#141414]/70 transition-colors duration-150 hover:bg-[#f6f6f4] hover:text-[#141414]"><Cog6ToothIcon className="h-[18px] w-[18px] text-[#141414]/40" />Settings</a><a href="/api/logout" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-medium text-red-600 transition-colors duration-150 hover:bg-red-50"><ArrowRightStartOnRectangleIcon className="h-[18px] w-[18px]" />Sign out</a></div>;
    $[38] = isDemoMode;
    $[39] = onClose;
    $[40] = settingsHref;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t16 || $[43] !== t18 || $[44] !== t19) {
    t20 = <div>{t16}{t18}{t19}</div>;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  return t20;
});

// ============================================
// DESKTOP POPOVER
// Content stays mounted to prevent re-renders and image reloading
// ============================================
function DesktopPopover({
  trigger,
  children,
  isOpen,
  onOpenChange
}: {
  trigger: React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [position, setPosition] = useState({
    top: 0,
    left: 0
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Track if panel has ever been opened (for lazy initial render)
  const hasBeenOpened = useRef(false);
  if (isOpen && !hasBeenOpened.current) {
    hasBeenOpened.current = true;
  }
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = 320;
      const panelHeight = 400;
      const padding = 16;
      let top = rect.bottom + 8;
      let left = rect.left;
      if (left + panelWidth > window.innerWidth - padding) {
        left = window.innerWidth - panelWidth - padding;
      }
      if (top + panelHeight > window.innerHeight - padding) {
        top = rect.top - panelHeight - 8;
        if (top < padding) top = padding;
      }
      if (left < padding) left = padding;
      setPosition({
        top,
        left
      });
    }
  }, []);
  const handleToggle = useCallback(() => {
    if (isOpen) {
      onOpenChange(false);
    } else {
      updatePosition();
      onOpenChange(true);
    }
  }, [isOpen, updatePosition, onOpenChange]);
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Update position when opened externally (e.g., via Cmd+K)
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node) && triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    const handleEscape = (e_0: KeyboardEvent) => {
      if (e_0.key === "Escape") handleClose();
    };
    const handleScroll = () => updatePosition();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen, handleClose, updatePosition]);
  const renderPanel = () => {
    if (typeof document === "undefined") return null;
    // Only render content after first open (lazy mount)
    if (!hasBeenOpened.current) return null;
    return createPortal(<div ref={panelRef} style={{
      position: "fixed",
      top: position.top,
      left: position.left,
      zIndex: 9999,
      // Keep mounted but visually hidden when closed
      visibility: isOpen ? "visible" : "hidden",
      pointerEvents: isOpen ? "auto" : "none"
    }} className={classNames("w-[320px] origin-top-left overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-900/5", "transition-all duration-200 ease-out", isOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-[0.98] translate-y-1")}>
                {children(handleClose)}
            </div>, document.body);
  };
  return <>
            <div ref={triggerRef} onClick={handleToggle}>
                {trigger}
            </div>
            {renderPanel()}
        </>;
}

// ============================================
// MOBILE DRAWER
// ============================================
function MobileDrawer(t0) {
  const $ = _c(16);
  const {
    trigger,
    children,
    isOpen,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== trigger) {
    t1 = <Drawer.Trigger asChild={true}><div>{trigger}</div></Drawer.Trigger>;
    $[0] = trigger;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex justify-center pb-1 pt-3"><div className="h-1 w-8 rounded-full bg-gray-200" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onOpenChange) {
    t4 = () => onOpenChange(false);
    $[4] = onOpenChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = children(t4);
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Drawer.Portal>{t2}<Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 flex max-h-[85vh] flex-col rounded-t-2xl bg-white outline-none">{t3}<div className="pb-safe flex-1 overflow-y-auto">{t5}</div></Drawer.Content></Drawer.Portal>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isOpen || $[12] !== onOpenChange || $[13] !== t1 || $[14] !== t6) {
    t7 = <Drawer.Root open={isOpen} onOpenChange={onOpenChange}>{t1}{t6}</Drawer.Root>;
    $[11] = isOpen;
    $[12] = onOpenChange;
    $[13] = t1;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

// ============================================
// MAIN COMPONENT
// ============================================
/**
 * SidebarAccountSelector - Account dropdown/drawer for the sidebar
 * Wrapped in memo to prevent re-renders from parent components.
 * Uses React Query for account caching to prevent refetches on navigation.
 */
const SidebarAccountSelector = memo(function SidebarAccountSelector({
  className
}: SidebarAccountSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    setIsConnectAccountModalOpened
  } = useModalContext();
  // Desktop uses LOCAL state for popover - not shared context
  // This prevents desktop clicks from opening mobile drawer
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isMobile = useIsMobile();
  const localizedPath = useLocalizedAppPath();
  const settingsHref = localizedPath("/app/settings");

  // Use React Query for cached accounts - prevents refetch on navigation
  const {
    data: accountsData,
    isLoading
  } = useGetAllAccountsForUser();
  const accounts = accountsData as AccountWithPlatform[] | null ?? null;

  // Track avatar preload state - prevents placeholder flash
  const [avatarsReady, setAvatarsReady] = useState(false);
  const preloadStartedRef = useRef(false);

  // Check if all avatars are in browser cache (synchronous check)
  const allAvatarsBrowserCached = useMemo(() => {
    if (!accounts || accounts.length === 0) return true;
    if (typeof window === "undefined") return false;
    return accounts.every(account => {
      if (!account.avatar_url) return true;
      const url = proxyImageUrl(account.avatar_url);
      const img = new window.Image();
      img.src = url;
      return img.complete && img.naturalWidth > 0;
    });
  }, [accounts]);

  // If all avatars are browser-cached, mark as ready immediately
  useEffect(() => {
    if (allAvatarsBrowserCached && !avatarsReady) {
      setAvatarsReady(true);
    }
  }, [allAvatarsBrowserCached, avatarsReady]);

  // Preload all avatar images if not already cached
  useEffect(() => {
    if (!accounts || accounts.length === 0) return;
    if (allAvatarsBrowserCached) return; // Already cached, no need to preload
    if (preloadStartedRef.current) return; // Already started preloading

    preloadStartedRef.current = true;
    const urls = accounts.filter(a => a.avatar_url).map(a_0 => proxyImageUrl(a_0.avatar_url));
    if (urls.length === 0) {
      setAvatarsReady(true);
      return;
    }
    let loadedCount = 0;
    urls.forEach(url_0 => {
      const img_0 = new window.Image();
      img_0.onload = img_0.onerror = () => {
        // Add to module cache for future checks
        preloadAvatar(url_0);
        loadedCount++;
        if (loadedCount === urls.length) {
          setAvatarsReady(true);
        }
      };
      img_0.src = url_0;
    });
  }, [accounts, allAvatarsBrowserCached]);
  const currentAccountId = useMemo(() => {
    const match = pathname.match(/\/app\/account\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  }, [pathname]);

  // Derive selected account from cached data - no state needed
  const selectedAccount = useMemo(() => {
    if (!accounts || accounts.length === 0) return null;

    // SECURITY: Only use accountId from URL if it exists in user's owned accounts
    // If URL contains unauthorized accountId, ignore it and fall back to stored/first account
    if (currentAccountId) {
      const current = accounts.find(acc => acc.id === currentAccountId);
      // Only set as selected if this account actually belongs to the user
      if (current) {
        setStoredSelectedAccountId(current.id);
        return current;
      }
      // If currentAccountId is NOT in user's accounts, don't use it - fall through to fallback
    }

    // Fallback: Use stored preference or first owned account
    const storedId = getStoredSelectedAccountId();
    const storedAccount = storedId ? accounts.find(acc_0 => acc_0.id === storedId) : null;
    const selected = storedAccount || accounts[0];
    if (selected) setStoredSelectedAccountId(selected.id);
    return selected;
  }, [accounts, currentAccountId]);
  const handleSelectAccount = useCallback((account_0: AccountWithPlatform) => {
    setStoredSelectedAccountId(account_0.id); // Persist selection
    router.push(`/app/account/${account_0.id}`);
  }, [router]);
  const handleConnectAccount = useCallback(() => {
    setIsConnectAccountModalOpened(true);
  }, [setIsConnectAccountModalOpened]);

  // Memoize trigger to prevent re-renders on open/close
  // Must be before conditional returns to satisfy Rules of Hooks
  const trigger = useMemo(() => <AccountTrigger account={selectedAccount} />, [selectedAccount]);

  // Memoize content render function - dependencies are stable callbacks
  const content = useCallback((closePopup: () => void) => <DropdownContent accounts={accounts} selectedAccount={selectedAccount} onSelectAccount={handleSelectAccount} onConnectAccount={handleConnectAccount} onClose={closePopup} settingsHref={settingsHref} />, [accounts, selectedAccount, handleSelectAccount, handleConnectAccount, settingsHref]);

  // Handle open state changes from popover
  const handleOpenChange = useCallback((newOpen: boolean) => {
    setIsPopoverOpen(newOpen);
  }, []);

  // Show skeleton while loading accounts OR while preloading avatars
  const showSkeleton = isLoading && !accounts || !avatarsReady && !allAvatarsBrowserCached;
  if (showSkeleton) {
    return <div className={classNames("relative w-full", className)}>
                <AccountSelectorSkeleton />
            </div>;
  }

  // If user has no accounts at all, don't render anything
  // (they'll use the "Connect Account" button in the sidebar)
  if (!accounts || accounts.length === 0) {
    return null;
  }

  // Render based on device type - no hydration mismatch since we always render something
  // The isMobile hook returns false on server and updates on client
  // On mobile, don't render anything - MobileAccountIndicator handles the account switcher drawer
  // The sidebar itself is hidden on mobile (hidden md:block), but we also skip rendering here
  // to prevent the drawer from rendering via Portal when isOpen=true from shared context
  if (isMobile) {
    return null;
  }
  return <div className={classNames("relative w-full", className)}>
            <DesktopPopover trigger={trigger} isOpen={isPopoverOpen} onOpenChange={handleOpenChange}>
                {content}
            </DesktopPopover>
        </div>;
});
export default SidebarAccountSelector;